import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@linaria/core';
import classNames from 'classnames';
import { HE_LOGO_HEIGHT, HE_LOGO_SVG, HE_LOGO_WIDTH } from '../utils/heLogo';

/**
 * The printed page, handed to the browser's own print dialog (which is where a
 * PDF can be saved from). Nothing here is visible on screen: the sheet is
 * portalled to <body> and only laid out in print media, where a body-level class
 * hides everything else on the page so the output is always this one sheet.
 *
 * Sizes are in millimetres against a known A4 portrait page rather than measured
 * from the viewport, so the map's fitted box is deterministic and cannot spill
 * onto a second page.
 */

// A4 portrait, millimetres.
const PAGE = { width: 210, height: 297 };
const MARGIN = 12;
const LOGO_WIDTH = 55;
const LOGO_GAP = 8;

const LOGO_HEIGHT = (LOGO_WIDTH * HE_LOGO_HEIGHT) / HE_LOGO_WIDTH;

const CONTENT_WIDTH = PAGE.width - MARGIN * 2;
const CONTENT_HEIGHT = PAGE.height - MARGIN * 2 - LOGO_HEIGHT - LOGO_GAP;

const LOGO_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  HE_LOGO_SVG,
)}`;

// Largest box of the given aspect ratio that fits the space below the logo.
function fitMapBox(aspectRatio) {
  let width = CONTENT_WIDTH;
  let height = width / aspectRatio;

  if (height > CONTENT_HEIGHT) {
    height = CONTENT_HEIGHT;
    width = height * aspectRatio;
  }

  return { width, height };
}

// Plain global class names: the body-level rule has to name the sheet, and the
// body class has to be togglable from outside any styled element.
const printingBodyClass = 'he-map-printing';
const printSheetClass = 'he-map-print-sheet';

const printSheet = css`
  display: none;

  :global() {
    /* Explicit dimensions rather than "A4 portrait": the orientation keyword
       gets normalised away by the CSS minifier, and these have to agree with
       the millimetre maths above. */
    @page {
      size: ${PAGE.width}mm ${PAGE.height}mm;
      margin: ${MARGIN}mm;
    }

    /* Only while a sheet is mounted, so an ordinary Ctrl+P of the page is
       untouched. Everything at the top level goes except the sheet itself,
       which sits alongside the app's own root. */
    @media print {
      body.${printingBodyClass} > *:not(.${printSheetClass}) {
        display: none !important;
      }

      body.${printingBodyClass} {
        background: #ffffff;
      }
    }
  }

  @media print {
    display: block;
    width: ${CONTENT_WIDTH}mm;
    font-family: 'articulat-cf', Tahoma, Verdana;
    color: #1a1a1a;
    /* The page box already carries the margin. */
    margin: 0;
    padding: 0;
    break-inside: avoid;

    .print-logo {
      display: block;
      width: ${LOGO_WIDTH}mm;
      height: ${LOGO_HEIGHT}mm;
      margin-bottom: ${LOGO_GAP}mm;
    }

    .print-map {
      position: relative;
      margin: 0 auto;
      overflow: hidden;
    }

    .print-map-image {
      display: block;
      width: 100%;
      height: 100%;
    }

    .print-scale,
    .print-copyright {
      position: absolute;
      bottom: 0;
      background-color: #ffffff;
      font-size: 7pt;
      line-height: 1.25;
      color: #000000;
      box-sizing: border-box;
      padding: 0.6mm 1.5mm;
    }

    .print-scale {
      left: 0;
      display: flex;
      align-items: center;
      gap: 1.5mm;
      white-space: nowrap;
      font-weight: 600;
    }

    .print-scale-bar {
      height: 1.8mm;
      border-left: 0.3mm solid #000000;
      border-right: 0.3mm solid #000000;
      border-bottom: 0.3mm solid #000000;
      box-sizing: border-box;
    }

    .print-copyright {
      right: 0;
      max-width: 70%;
      text-align: right;
    }
  }
`;

export default function MapPrintSheet({ image, scale, copyrightText, onDone }) {
  const [imageReady, setImageReady] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Wait for the map image to decode before printing, or the dialog can open on
  // an empty page.
  useEffect(() => {
    if (!imageReady) return undefined;

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      onDoneRef.current?.();
    };

    document.body.classList.add(printingBodyClass);
    window.addEventListener('afterprint', finish);

    window.print();
    // Chrome blocks in print() and then fires afterprint; other browsers return
    // immediately. Either way the sheet is torn down exactly once.
    const timer = setTimeout(finish, 500);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', finish);
      document.body.classList.remove(printingBodyClass);
    };
  }, [imageReady]);

  if (!image) return null;

  const box = fitMapBox(image.width / image.height);
  const barWidth =
    scale?.width && scale?.mapWidth
      ? (scale.width / scale.mapWidth) * box.width
      : 0;

  return createPortal(
    <div aria-hidden="true" className={classNames(printSheet, printSheetClass)}>
      <img alt="" className="print-logo" src={LOGO_SRC} />
      <div
        className="print-map"
        style={{ width: `${box.width}mm`, height: `${box.height}mm` }}
      >
        <img
          alt=""
          className="print-map-image"
          onError={() => setImageReady(true)}
          onLoad={() => setImageReady(true)}
          src={image.src}
        />
        {scale?.label && barWidth > 0 && (
          <div className="print-scale">
            <span>{scale.label}</span>
            <span
              className="print-scale-bar"
              style={{ width: `${barWidth}mm` }}
            />
          </div>
        )}
        {copyrightText && (
          <div className="print-copyright">{`© ${copyrightText}`}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
